/**
 * Verification Info Form
 * FEATURE-015: Checkout Flow Redesign
 * Collects name, DOB, and phone before identity verification
 */
import React, { useState } from "react";

import Loader from "../loader/loader.component";

import "./verification.scss";

export interface VerificationInfoFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phoneNumber: string;
}

interface VerificationInfoProps {
  onSubmit: (formData: VerificationInfoFormData) => void;
  loading?: boolean;
}

const VerificationInfo: React.FC<VerificationInfoProps> = ({
  onSubmit,
  loading = false,
}) => {
  const [errors, setErrors] = useState(false);

  const [formValues, setFormValues] = useState<VerificationInfoFormData>({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    phoneNumber: "",
  });

  const onFormValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormValues({ ...formValues, phoneNumber: formatted });
  };

  const validateForm = (): boolean => {
    if (!formValues.firstName.trim()) return false;
    if (!formValues.lastName.trim()) return false;
    if (!formValues.dateOfBirth) return false;

    // Phone should be 10 digits
    const phoneDigits = formValues.phoneNumber.replace(/\D/g, "");
    if (phoneDigits.length !== 10) return false;

    return true;
  };

  const handleSubmit = () => {
    setErrors(true);

    if (!validateForm()) {
      return;
    }

    onSubmit(formValues);
  };

  return (
    <section className="verification-info">
      <div className="container">
        <div className="row" style={{ display: "flex", justifyContent: "center" }}>
          <div className="col form">
            <div className="row">
              <form className="col" name="verificationInfoForm">
                <div className="form-group title">
                  <span className="fa fa-id-card"></span>
                  <label className="text-center call-txt">
                    Verify Your Identity
                  </label>
                  <p className="subtitle">
                    Please provide your legal name and date of birth exactly as they appear on your government-issued ID.
                  </p>
                </div>

                <div className="form-group">
                  <div className="col-md-12 pad">
                    <label htmlFor="firstName">Legal First Name</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      className="form-control"
                      placeholder="First name as shown on ID"
                      autoComplete="given-name"
                      value={formValues.firstName}
                      onChange={onFormValueChanged}
                    />
                    {errors && !formValues.firstName.trim() && (
                      <span className="input-error">First name is required</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="col-md-12 pad">
                    <label htmlFor="lastName">Legal Last Name</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      className="form-control"
                      placeholder="Last name as shown on ID"
                      autoComplete="family-name"
                      value={formValues.lastName}
                      onChange={onFormValueChanged}
                    />
                    {errors && !formValues.lastName.trim() && (
                      <span className="input-error">Last name is required</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="col-md-12 pad">
                    <label htmlFor="dateOfBirth">Date of Birth</label>
                    <input
                      id="dateOfBirth"
                      name="dateOfBirth"
                      type="date"
                      className="form-control"
                      autoComplete="bday"
                      value={formValues.dateOfBirth}
                      onChange={onFormValueChanged}
                    />
                    {errors && !formValues.dateOfBirth && (
                      <span className="input-error">Date of birth is required</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="col-md-12 pad">
                    <label htmlFor="phoneNumber">Mobile Phone Number</label>
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      className="form-control"
                      placeholder="(555) 555-5555"
                      autoComplete="tel"
                      value={formValues.phoneNumber}
                      onChange={onPhoneChange}
                    />
                    {errors && formValues.phoneNumber.replace(/\D/g, "").length !== 10 && (
                      <span className="input-error">Valid phone number is required</span>
                    )}
                    <small className="form-text text-muted">
                      We'll send a verification code to this number for high-value transactions.
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <div className="col-md-12 pad">
                    {loading ? (
                      <Loader />
                    ) : (
                      <button
                        type="button"
                        className="btn-register"
                        onClick={handleSubmit}
                      >
                        Continue to Document Verification
                      </button>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="col-md-12 pad info-box">
                    <p>
                      <strong>What happens next?</strong>
                    </p>
                    <ul>
                      <li>You'll take a photo of your government-issued ID</li>
                      <li>You'll take a quick selfie for identity matching</li>
                      <li>Verification typically takes less than 2 minutes</li>
                    </ul>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerificationInfo;
